import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Check, ChevronRight, Loader2, MessageSquare, Mic, MicOff, Send, Sparkles, Volume2, VolumeX, X,
} from 'lucide-react';
import { api } from '@/shared/api/axios';
import { SAME_ORIGIN_API_BASE } from '@/shared/api/sameOrigin';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useUser } from '@/shared/hooks/useAuth';
import { useSpeechInput, useSpeechOutput, type VoiceLang } from '@/shared/hooks/useVoice';

// Smart Copilot (2026-10-06): answers come from the CRM's real data
// (POST /api/ai/copilot, streamed). Records it found show as cards that open
// the record; follow-ups / tasks it prepares are saved only when the employee
// presses Confirm. Voice: speak a question (Hindi / English), hear the answer.

type Card = { kind: string; title: string; subtitle?: string; amount?: number; link: string };
type Action = { kind: 'followup' | 'task'; label: string; body: Record<string, unknown>; endpoint: string; state?: 'idle' | 'saving' | 'saved' | 'error'; error?: string };
type ChatMessage = {
  role: 'user' | 'assistant'; content: string; failed?: boolean;
  status?: string; cards?: Card[]; actions?: Action[]; pending?: boolean;
};

const SUGGESTIONS = [
  'Aaj ke follow-ups kya hain?',
  'Is mahine meri sales vs target?',
  'Dispatch mein kya pending hai?',
  'Kaunse quotations abhi open hain?',
];

function professionalText(content: string) {
  return content.replace(/\*\*(.*?)\*\*/g, '$1').replace(/`([^`]+)`/g, '$1').replace(/^\s*[-*]\s+/gm, '• ').trim();
}
const inr = (n?: number) => (n ? `₹${Math.round(n).toLocaleString('en-IN')}` : '');

/**
 * Remembers that the user has collapsed the promo card to a compact button.
 *
 * Persisted because the alternative is re-covering the corner of every page on
 * each reload, which is the behaviour being fixed. Reads are wrapped: private
 * mode and blocked storage throw on access rather than returning null.
 */
const MINIMISED_KEY = 'zynx.copilot.minimised';
const VOICE_KEY = 'zynx.copilot.voice';

function readMinimised(): boolean {
  try {
    return localStorage.getItem(MINIMISED_KEY) !== '0';
  } catch {
    return true;
  }
}
function readVoice(): { lang: VoiceLang; speak: boolean } {
  try {
    const v = JSON.parse(localStorage.getItem(VOICE_KEY) || '{}');
    return { lang: v.lang === 'hi-IN' ? 'hi-IN' : 'en-IN', speak: !!v.speak };
  } catch {
    return { lang: 'en-IN', speak: false };
  }
}

const friendlyError = (status?: number, code?: string) => (
  code === 'AI_PROVIDER_NOT_CONFIGURED' ? 'Copilot is not configured on the running server. Ask your administrator to check its AI settings.'
    : code === 'AI_AUTH_FAILED' ? 'The AI provider rejected the key. Ask your administrator to update it.'
      : code === 'AI_CREDITS_REQUIRED' ? 'The AI plan has run out of credit. Ask your administrator.'
        : status === 401 ? 'Your session has expired. Please sign in again.'
          : status === 429 || code === 'AI_RATE_LIMITED' ? 'Copilot is busy. Please try again shortly.'
            : 'Copilot could not connect to the AI service. Please try again shortly.'
);

export function RightCopilot() {
  const user = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  // The expanded promo card is 280x152 and fixed to the bottom-right corner,
  // so on every one of the app's screens it sat on top of whatever occupied
  // that corner — the Negotiation column on Deals, the Open Tasks card on the
  // dashboard. Collapsing to a compact button returns that area to the page
  // while keeping the assistant one click away.
  const [isMinimised, setIsMinimised] = useState(readMinimised);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const conversationEnd = useRef<HTMLDivElement>(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingRetry, setBriefingRetry] = useState(0);
  const [voice, setVoice] = useState(readVoice);
  const out = useSpeechOutput();
  const sendRef = useRef<(text?: string) => void>(() => {});
  const mic = useSpeechInput((text, final) => {
    setMessage(text);
    if (final && text) sendRef.current(text);
  });

  useEffect(() => {
    if (!user?.id || briefingRetry === 0) return;
    let active = true;
    const controller = new AbortController();
    setMessage('');
    setMessages([]);
    setBriefingLoading(true);
    api.get<{ reply: string }>('/api/ai/startup-briefing', { signal: controller.signal, timeout: 60000 })
      .then(({ data }) => {
        if (active) setMessages([{ role: 'assistant', content: data.reply }]);
      })
      .catch(() => {
        if (active) setMessages([{ role: 'assistant', content: 'Your daily briefing could not be loaded. Use Daily briefing above to retry.', failed: true }]);
      })
      .finally(() => { if (active) setBriefingLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [user?.id, briefingRetry]);
  useEffect(() => {
    if (messages.length === 1 && messages[0].role === 'assistant') {
      conversationEnd.current?.parentElement?.scrollTo({ top: 0 });
      return;
    }
    conversationEnd.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isSending, isOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(MINIMISED_KEY, isMinimised ? '1' : '0');
    } catch {
      // Storage unavailable — the preference simply does not survive a reload.
    }
  }, [isMinimised]);
  useEffect(() => {
    try { localStorage.setItem(VOICE_KEY, JSON.stringify(voice)); } catch { /* not kept */ }
  }, [voice]);

  const patchLast = (fn: (m: ChatMessage) => ChatMessage) => setMessages((list) => {
    const copy = [...list];
    const i = copy.length - 1;
    if (i >= 0 && copy[i].role === 'assistant') copy[i] = fn(copy[i]);
    return copy;
  });

  /** Old plain chat — used when the server's AI is not the Claude provider. */
  const plainChat = async (text: string, history: Array<{ role: string; content: string }>) => {
    try {
      const { data } = await api.post('/api/ai/chat', { message: text, history }, { timeout: 120_000 });
      patchLast((m) => ({ ...m, pending: false, status: undefined, content: data?.reply || data?.message || 'I could not prepare a response. Please try again.' }));
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      const code = isAxiosError(error) ? error.response?.data?.code : undefined;
      patchLast((m) => ({ ...m, pending: false, status: undefined, failed: true, content: friendlyError(status, code) }));
    }
  };

  const sendMessage = async (spoken?: string) => {
    const text = (spoken ?? message).trim();
    if (!text || isSending || briefingLoading) return;
    out.cancel();
    const history = messages.filter((item) => !item.failed && item.content).slice(-8).map(({ role, content }) => ({ role, content }));
    setMessages((current) => [...current, { role: 'user', content: text }, { role: 'assistant', content: '', pending: true, status: 'Thinking…' }]);
    setMessage('');
    setIsSending(true);
    let answer = '';
    try {
      const resp = await fetch(`${SAME_ORIGIN_API_BASE}/api/ai/copilot`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, history, page: location.pathname }),
      });
      if (resp.status === 409 || resp.status === 503) {
        const body = await resp.json().catch(() => ({}));
        if (resp.status === 409) { await plainChat(text, history); return; }
        patchLast((m) => ({ ...m, pending: false, status: undefined, failed: true, content: friendlyError(resp.status, body?.code) }));
        return;
      }
      if (!resp.ok || !resp.body) {
        const body = await resp.json().catch(() => ({}));
        patchLast((m) => ({ ...m, pending: false, status: undefined, failed: true, content: body?.error || friendlyError(resp.status, body?.code) }));
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ') || line === 'data: [DONE]') continue;
          let ev: { type: string; text?: string; cards?: Card[]; action?: Action; code?: string };
          try { ev = JSON.parse(line.slice(6)); } catch { continue; }
          if (ev.type === 'status') patchLast((m) => ({ ...m, status: ev.text }));
          else if (ev.type === 'cards' && ev.cards) patchLast((m) => ({ ...m, cards: [...(m.cards || []), ...ev.cards!] }));
          else if (ev.type === 'action' && ev.action) patchLast((m) => ({ ...m, actions: [...(m.actions || []), { ...ev.action!, state: 'idle' }] }));
          else if (ev.type === 'content') { answer += ev.text || ''; patchLast((m) => ({ ...m, content: answer })); }
          else if (ev.type === 'error') patchLast((m) => ({ ...m, failed: true, content: ev.text || friendlyError(undefined, ev.code) }));
        }
      }
      patchLast((m) => ({ ...m, pending: false, status: undefined }));
      if (voice.speak && answer) out.speak(professionalText(answer));
    } catch {
      patchLast((m) => ({ ...m, pending: false, status: undefined, failed: true, content: friendlyError() }));
    } finally {
      setIsSending(false);
    }
  };
  sendRef.current = (t?: string) => { void sendMessage(t); };

  const confirmAction = async (msgIndex: number, actIndex: number) => {
    const setState = (patch: Partial<Action>) => setMessages((list) => list.map((m, i) => (i !== msgIndex ? m : { ...m, actions: m.actions?.map((a, j) => (j === actIndex ? { ...a, ...patch } : a)) })));
    const action = messages[msgIndex]?.actions?.[actIndex];
    if (!action) return;
    setState({ state: 'saving', error: undefined });
    try {
      await api.post(action.endpoint, action.body);
      setState({ state: 'saved' });
    } catch (error) {
      setState({ state: 'error', error: (isAxiosError(error) && error.response?.data?.error) || 'Could not save it.' });
    }
  };
  const dismissAction = (msgIndex: number, actIndex: number) => setMessages((list) => list.map((m, i) => (i !== msgIndex ? m : { ...m, actions: m.actions?.filter((_, j) => j !== actIndex) })));

  // Compact launcher — roughly a seventh of the promo card's footprint.
  if (!isOpen && isMinimised) return (
    <button
      type="button"
      onClick={() => setIsOpen(true)}
      aria-label="Open ZYNX Copilot"
      title="Zynx Copilot"
      className="group fixed bottom-6 right-6 z-[90] flex items-center gap-2 rounded-full bg-gradient-brand px-4 py-3 text-white shadow-[0_12px_32px_rgba(91,45,225,0.32)] transition-transform hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-primary/30"
    >
      <Sparkles className="h-5 w-5 shrink-0" />
      {/* Label expands on hover/focus so the collapsed state stays small but
          still identifies itself rather than being a bare mystery icon. */}
      <span className="max-w-0 overflow-hidden whitespace-nowrap text-body-sm font-semibold transition-all duration-200 group-hover:max-w-[120px] group-focus:max-w-[120px]">
        Zynx Copilot
      </span>
    </button>
  );

  if (!isOpen) return (
    <div className="fixed bottom-6 right-6 z-[90] w-[280px] overflow-hidden rounded-2xl bg-gradient-brand text-white shadow-[0_18px_44px_rgba(91,45,225,0.32)] transition-transform hover:-translate-y-1">
      {/* Nested inside a div rather than left as one big button: a button
          cannot legally contain the dismiss button, and making the whole card
          clickable with no way to dismiss it is the original problem. */}
      <button
        type="button"
        onClick={() => setIsMinimised(true)}
        aria-label="Minimise ZYNX Copilot"
        title="Minimise"
        className="absolute right-2 top-2 rounded-md p-1.5 text-white/80 hover:bg-white/20 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label="Open ZYNX Copilot"
        className="block w-full p-4 pr-10 text-left focus:outline-none focus:ring-4 focus:ring-primary/30"
      >
        <span className="flex items-center gap-2 text-body-sm font-semibold"><Sparkles className="h-4 w-4" /> Zynx Copilot</span>
        <span className="mt-2 block text-sm leading-relaxed text-white/95">Ask anything about your customers, orders, dispatch or sales — type or speak.</span>
        <span className="mt-3 block rounded-lg bg-white/20 py-2 text-center text-sm font-semibold">Open assistant →</span>
      </button>
    </div>
  );

  return (
    <aside className="fixed bottom-5 right-5 z-[90] flex h-[min(680px,calc(100vh-40px))] w-[min(420px,calc(100vw-40px))] flex-col overflow-hidden rounded-2xl border border-primary/10 bg-white shadow-[0_24px_60px_rgba(30,27,75,0.24)]">
      <div className="flex items-center justify-between bg-gradient-brand px-4 py-3.5 text-white">
        <div className="flex items-center gap-2"><span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/20"><Sparkles className="h-4 w-4" /></span><div><p className="text-sm font-semibold">Zynx Copilot</p><p className="text-xs text-white/80">Answers from your live CRM data</p></div></div>
        <div className="flex items-center gap-1">
          {out.supported && (
            <button type="button" onClick={() => { if (voice.speak) out.cancel(); setVoice((v) => ({ ...v, speak: !v.speak })); }} className="rounded-md p-1.5 hover:bg-white/15" aria-label={voice.speak ? 'Stop reading answers aloud' : 'Read answers aloud'} title={voice.speak ? 'Answers are read aloud' : 'Read answers aloud'}>
              {voice.speak ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4 opacity-70" />}
            </button>
          )}
          <button type="button" onClick={() => { out.cancel(); mic.stop(); setIsOpen(false); }} className="rounded-md p-1.5 hover:bg-white/15" aria-label="Close ZYNX Copilot"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="flex items-center justify-between border-b border-slate-100 bg-white px-4 py-2">
        <button type="button" disabled={isSending || messages.length === 0} onClick={() => { out.cancel(); setMessages([]); }} className="text-xs font-semibold text-slate-500 disabled:opacity-40">New chat</button>
        <button type="button" disabled={briefingLoading || isSending} onClick={() => setBriefingRetry((value) => value + 1)} className="text-xs font-semibold text-primary disabled:opacity-50">Daily briefing</button>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-[#faf9ff] p-4">
        {briefingLoading ? <p role="status" className="text-sm text-slate-700">Reviewing work, schedules and critical items…</p> : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-3 text-center">
            <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><MessageSquare className="h-6 w-6" /></span>
            <p className="font-semibold text-slate-900">How can I help?</p>
            <p className="mt-1 text-sm leading-relaxed text-slate-500">Ask in English or Hindi — about clients, quotations, invoices, dispatch, stock, prices or your targets. I can also set follow-ups and tasks for you.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => <button key={s} type="button" onClick={() => void sendMessage(s)} className="rounded-full border border-primary/20 bg-white px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/5">{s}</button>)}
            </div>
          </div>
        ) : messages.map((item, index) => (
          item.role === 'user' ? (
            <div key={`u-${index}`} className="ml-auto max-w-[88%] whitespace-pre-wrap break-words rounded-2xl bg-primary px-3 py-2.5 text-sm font-medium leading-relaxed text-white shadow-sm">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-white/70">You</span>{item.content}
            </div>
          ) : (
            <div key={`a-${index}`} className="max-w-[94%] space-y-2">
              {(item.content || item.pending) && (
                <div className={`whitespace-pre-wrap break-words rounded-2xl px-3 py-2.5 text-sm leading-relaxed shadow-sm ${item.failed ? 'bg-rose-50 text-rose-700' : 'bg-white text-slate-700'}`}>
                  {item.content ? professionalText(item.content) : <span className="flex items-center gap-2 text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" />{item.status || 'Thinking…'}</span>}
                  {item.content && !item.pending && out.supported && !item.failed && (
                    <button type="button" onClick={() => out.speak(professionalText(item.content))} className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-primary" aria-label="Read this answer aloud"><Volume2 className="h-3 w-3" /> Listen</button>
                  )}
                </div>
              )}
              {item.content && item.pending && item.status && <p className="flex items-center gap-1.5 pl-1 text-[11px] text-slate-400"><Loader2 className="h-3 w-3 animate-spin" />{item.status}</p>}
              {item.actions?.map((a, j) => (
                <div key={`act-${j}`} className="rounded-xl border border-primary/25 bg-white p-3 shadow-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">{a.kind === 'followup' ? 'Follow-up ready' : 'Task ready'}</p>
                  <p className="mt-0.5 text-sm font-medium text-slate-800">{a.label}</p>
                  {typeof a.body.followupNote === 'string' && <p className="mt-0.5 text-xs text-slate-500">{a.body.followupNote}</p>}
                  {typeof a.body.description === 'string' && a.body.description && <p className="mt-0.5 text-xs text-slate-500">{a.body.description}</p>}
                  <div className="mt-2 flex items-center gap-2">
                    {a.state === 'saved' ? <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600"><Check className="h-3.5 w-3.5" /> Saved</span> : (
                      <>
                        <Button type="button" size="sm" disabled={a.state === 'saving'} onClick={() => void confirmAction(index, j)}>{a.state === 'saving' ? 'Saving…' : 'Confirm'}</Button>
                        <button type="button" disabled={a.state === 'saving'} onClick={() => dismissAction(index, j)} className="text-xs font-medium text-slate-500 hover:text-slate-700">Dismiss</button>
                      </>
                    )}
                  </div>
                  {a.state === 'error' && <p className="mt-1 text-xs text-rose-600">{a.error}</p>}
                </div>
              ))}
              {!!item.cards?.length && (
                <div className="space-y-1.5">
                  {item.cards.slice(0, 8).map((c, j) => (
                    <button key={`c-${j}`} type="button" onClick={() => navigate(c.link)} className="flex w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-left transition hover:border-primary/40 hover:bg-primary/5">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold text-slate-800">{c.title}</p>
                        {c.subtitle && <p className="truncate text-[11px] text-slate-500">{c.subtitle}</p>}
                      </div>
                      {!!c.amount && <span className="shrink-0 text-xs font-semibold tabular-nums text-slate-700">{inr(c.amount)}</span>}
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
                    </button>
                  ))}
                  {item.cards.length > 8 && <p className="pl-1 text-[11px] text-slate-400">+{item.cards.length - 8} more</p>}
                </div>
              )}
            </div>
          )
        ))}
        <div ref={conversationEnd} />
      </div>
      <div className="border-t border-slate-100 bg-white p-3">
        {mic.listening && <p className="mb-2 flex items-center gap-2 text-xs font-medium text-rose-600"><span className="h-2 w-2 animate-pulse rounded-full bg-rose-500" /> Listening ({voice.lang === 'hi-IN' ? 'हिंदी' : 'English'})… tap the mic to stop</p>}
        {mic.error && !mic.listening && <p className="mb-2 text-xs text-amber-600">{mic.error}</p>}
        <div className="flex gap-2">
          <Input value={message} onChange={(event) => setMessage(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void sendMessage(); }} placeholder={mic.listening ? 'Speak now…' : 'Ask or tap the mic…'} aria-label="Message ZYNX Copilot" />
          {mic.supported && (
            <>
              <button type="button" onClick={() => setVoice((v) => ({ ...v, lang: v.lang === 'en-IN' ? 'hi-IN' : 'en-IN' }))} disabled={mic.listening} className="shrink-0 rounded-lg border border-slate-200 px-2 text-[11px] font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50" title="Voice language" aria-label="Switch voice language">{voice.lang === 'hi-IN' ? 'हि' : 'EN'}</button>
              <button type="button" onClick={() => (mic.listening ? mic.stop() : mic.start(voice.lang))} disabled={isSending || briefingLoading} className={`shrink-0 rounded-lg px-3 transition ${mic.listening ? 'bg-rose-500 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'} disabled:opacity-50`} aria-label={mic.listening ? 'Stop listening' : 'Speak your question'}>
                {mic.listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
            </>
          )}
          <Button type="button" onClick={() => void sendMessage()} disabled={!message.trim() || isSending || briefingLoading} className="shrink-0 px-3" aria-label="Send message"><Send className="h-4 w-4" /></Button>
        </div>
      </div>
    </aside>
  );
}
